import React from 'react';
import styled from 'styled-components';
import { Crown, ShieldCheck, ShieldX, Plus } from 'lucide-react';
import { CompanyUserDto } from '@/services/companyUserService';
import { 
  Section, SectionHeader, SectionTitleWrapper, SectionIcon, 
  SectionTitle, SectionSubtitle, EmptyActiveSpaces, 
  EmptyIconWrapper, EmptyTitle, EmptyDescription, fadeIn, 
  LoadingState, LoadingSpinner 
} from '../../SharedStyles';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import Avatar from '@/common/components/avatar/Avatar';

interface MasterTabProps {
  masterUsers: CompanyUserDto[];
  isLoadingUsers: boolean;
  masterActionLoading: number | null;
  handleRemoveMaster: (companyUserId: number) => void;
  setShowAddMasterPanel: (show: boolean) => void;
  showAddMasterPanel: boolean;
}

const MasterTab: React.FC<MasterTabProps> = ({
  masterUsers,
  isLoadingUsers,
  masterActionLoading,
  handleRemoveMaster,
  setShowAddMasterPanel,
  showAddMasterPanel
}) => {
  return (
    <Section>
      <SectionHeader>
        <SectionTitleWrapper>
          <SectionIcon $variant="warning">
            <Crown size={20} />
          </SectionIcon>
          <div>
            <SectionTitle>현재 마스터</SectionTitle>
            <SectionSubtitle>회사 마스터 권한을 가진 팀원 목록입니다</SectionSubtitle>
          </div>
        </SectionTitleWrapper>
        <AddMasterButton onClick={() => setShowAddMasterPanel(!showAddMasterPanel)}>
          <Plus size={18} />
          <span>마스터 추가</span>
        </AddMasterButton>
      </SectionHeader>

      {isLoadingUsers ? (
        <LoadingState>
          <LoadingSpinner />
          <span>마스터 목록을 불러오는 중...</span>
        </LoadingState>
      ) : masterUsers.length > 0 ? (
        <TableWrapper>
          <TableHeader>
            <HeaderCell $flex={1}>사용자</HeaderCell>
            <HeaderCell $width="140px">직위</HeaderCell>
            <HeaderCell $width="100px">권한</HeaderCell>
            <HeaderCell $width="140px">액션</HeaderCell>
          </TableHeader>
          {masterUsers.map((member, index) => (
            <TableRow key={member.id} $delay={index * 0.03}>
              <RowCell $flex={1}>
                <UserCellContent>
                  <Avatar alt={member.name || '사용자'} isUser={true} sizes="md" />
                  <UserTextInfo>
                    <UserName>{member.name || '이름 없음'}</UserName>
                    <UserEmail>{member.email || '이메일 없음'}</UserEmail>
                  </UserTextInfo>
                </UserCellContent>
              </RowCell>
              <RowCell $width="140px">
                <PositionText>{member.position || '-'}</PositionText>
              </RowCell>
              <RowCell $width="100px">
                <MasterBadge>
                  <Crown size={12} />
                  마스터
                </MasterBadge>
              </RowCell>
              <RowCell $width="140px">
                {masterUsers.length === 1 ? (
                  <LastMasterNotice>
                    <ShieldCheck size={14} />
                    <span>해제 불가</span>
                  </LastMasterNotice>
                ) : (
                  <MasterActionButton
                    onClick={() => handleRemoveMaster(member.id)}
                    disabled={masterActionLoading === member.id}
                  >
                    {masterActionLoading === member.id ? (
                      <LoadingSpinner />
                    ) : (
                      <>
                        <ShieldX size={14} />
                        <span>마스터 해제</span>
                      </>
                    )}
                  </MasterActionButton>
                )}
              </RowCell>
            </TableRow>
          ))}
        </TableWrapper>
      ) : (
        <EmptyActiveSpaces>
          <EmptyIconWrapper>
            <Crown size={48} />
          </EmptyIconWrapper>
          <EmptyTitle>마스터 정보를 불러오는 중...</EmptyTitle>
          <EmptyDescription>
            회사에는 최소 1명의 마스터가 필요합니다.
          </EmptyDescription>
        </EmptyActiveSpaces>
      )}
    </Section>
  );
};

const AddMasterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border: none;
  border-radius: 10px;
  ${typography.paragraph.p2.default}
  font-weight: 500;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(245, 158, 11, 0.3);
  }
`;

const TableWrapper = styled.div`
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 12px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem 1.25rem;
  background: ${semanticColors.background.tertiary};
  border-bottom: 1px solid ${semanticColors.border.primary};
  gap: 1rem;
`;

const HeaderCell = styled.div<{ $flex?: number; $width?: string }>`
  ${props => props.$flex ? `flex: ${props.$flex};` : ''}
  ${props => props.$width ? `width: ${props.$width}; flex-shrink: 0;` : ''}
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.tertiary};
`;

const TableRow = styled.div<{ $delay: number }>`
  display: flex;
  align-items: center;
  padding: 0.875rem 1.25rem;
  gap: 1rem;
  border-bottom: 1px solid ${semanticColors.border.primary};
  background: ${semanticColors.background.secondary};
  animation: ${fadeIn} 0.4s ease-out ${props => props.$delay}s both;
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${semanticColors.background.tertiary};
  }
`;

const RowCell = styled.div<{ $flex?: number; $width?: string }>`
  ${props => props.$flex ? `flex: ${props.$flex}; min-width: 0;` : ''}
  ${props => props.$width ? `width: ${props.$width}; flex-shrink: 0;` : ''}
  display: flex;
  align-items: center;
`;

const UserCellContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
`;

const UserTextInfo = styled.div`
  min-width: 0;
`;

const UserName = styled.p`
  ${typography.paragraph.p2.default}
  font-weight: 600;
  color: ${semanticColors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserEmail = styled.p`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PositionText = styled.span`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
`;

const MasterBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.625rem;
  background: ${semanticColors.background.warning.secondary};
  border-radius: 6px;
  ${typography.caption.caption2}
  color: ${semanticColors.text.onWarning.primary};
  font-weight: 600;
`;

const LastMasterNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
`;

const MasterActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  border: none;
  border-radius: 8px;
  ${typography.caption.caption1.medium}
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${semanticColors.background.danger.secondary};
  color: ${semanticColors.text.onDanger.secondary};
    
  &:hover:not(:disabled) {
    background: ${semanticColors.background.danger.primary};
    color: #fff;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default MasterTab;
